import * as THREE from 'three/webgpu';
import type { PlayerController } from '../../engine/character/PlayerController';
import type { Capabilities } from '../../engine/core/capabilities';
import { flags } from '../../engine/core/flags';
import { saveSettings, settings } from '../../engine/core/settings';
import type { DebugUI } from '../../engine/debug/DebugUI';
import type { Stats } from '../../engine/debug/Stats';

/** ?cam=x,y,z,qx,qy,qz,qw,yaw,pitch — restores a camera exactly (in fly mode). */
export function restoreBookmark(player: PlayerController): void {
  const cam = flags.strOrNull('cam');
  if (!cam) return;
  const v = cam.split(',').map(Number);
  if (v.length < 9 || v.some((n) => !Number.isFinite(n))) return;
  const pos = new THREE.Vector3(v[0], v[1], v[2]);
  const q = new THREE.Quaternion(v[3], v[4], v[5], v[6]).normalize();
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
  player.teleport(pos, up);
  player.body.copy(q);
  player.yaw = v[7];
  player.pitch = v[8];
  player.mode = 'fly';
}

function bookmark(player: PlayerController): string {
  const p = player.position;
  const q = player.body;
  const parts = [p.x, p.y, p.z].map((n) => n.toFixed(3));
  parts.push(...[q.x, q.y, q.z, q.w, player.yaw, player.pitch].map((n) => n.toFixed(5)));
  const url = new URL(location.href);
  url.searchParams.set('cam', parts.join(','));
  return url.toString();
}

export function buildDebugPanel(
  debug: DebugUI,
  player: PlayerController,
  caps: Capabilities,
  stats: Stats,
  camera: THREE.PerspectiveCamera,
): void {
  const pf = debug.folder('Player', true);
  const readout = {
    mode: '',
    g: 0,
    speed: 0,
  };
  pf.addBinding(readout, 'mode', { readonly: true });
  pf.addBinding(readout, 'g', { readonly: true, format: (v: number) => `${(v / 9.81).toFixed(3)} g` });
  pf.addBinding(readout, 'speed', { readonly: true, format: (v: number) => `${v.toFixed(1)} m/s` });
  pf.addBinding(player, 'flySpeed', { min: 2, max: 20000, label: 'fly speed' });
  pf.addBinding(settings, 'reorientRate', { min: 20, max: 360, label: 'reorient °/s' }).on(
    'change',
    saveSettings,
  );
  pf.addBinding(settings, 'fov', { min: 50, max: 110, step: 1, label: 'field of view' }).on('change', () => {
    camera.fov = settings.fov;
    camera.updateProjectionMatrix();
    saveSettings();
  });
  pf.addBinding(settings, 'mouseSensitivity', { min: 0.2, max: 3, label: 'mouse' }).on(
    'change',
    saveSettings,
  );
  pf.addButton({ title: 'Copy camera bookmark URL' }).on('click', () => {
    const url = bookmark(player);
    navigator.clipboard?.writeText(url).catch(() => undefined);
    console.info('[innerworlds] bookmark', url);
  });
  setInterval(() => {
    readout.mode = player.mode + (player.grounded ? ' (grounded)' : '');
    readout.g = player.gravity.length();
    readout.speed = player.velocity.length();
  }, 200);

  const rf = debug.folder('Render');
  rf.addBinding(settings, 'showStats', { label: 'stats' }).on('change', () => {
    stats.setVisible(settings.showStats);
    saveSettings();
  });
  const gpu = debug.folder('GPU');
  gpu.addBinding({ adapter: caps.adapter }, 'adapter', { readonly: true });
  gpu.addBinding({ f32filter: caps.float32Filterable }, 'f32filter', { readonly: true, label: 'f32 filter' });
  gpu.addBinding({ timestamps: caps.timestampQuery }, 'timestamps', { readonly: true });
}
